import { useState, useEffect, useCallback, useRef } from 'react';
import groupBy from 'lodash.groupby';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import {
  PermissionEnum,
  PermissionDenied,
  Autocomplete,
  IconLoader,
  DateInputRange,
  formatDate,
  IconSearch,
  IconClose,
  InputText,
  InputSize,
  usePrevious,
  ToggleInput,
  IconChangeLogUpdated,
  IconChangeLogAdded,
  IconChangeLogDeleted,
  IconArrowRight,
} from '@ui/src';
import Highlight from '@ui/src/components/autocomplete/highlight';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetChangeLogsQuery,
  useLazyGetChangeLogByIdQuery,
} from './store/service';
import { clearChangeLogs } from './store/slice';
import {
  useLazyGetEmployeesBaseDictionariesQuery,
  useLazyGetEmployeesDictionariesQuery,
} from '../../store/dictionaries/service';
import { ChangeLog } from './types';
import EntitySelector from './entity-selector';

import cn from './styles.module.css';

let entityIdTimeout: NodeJS.Timeout;

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const getSelectedChangeLogFields = (selectedChangeLog: ChangeLog) => {
  const fields: { name: string; updated: boolean; number: number }[] = [];

  const prevChangeLogs = selectedChangeLog.prev;
  const currentChangeLogs = selectedChangeLog.current;

  Object.keys(currentChangeLogs)?.forEach((item) => {
    let updated = false;
    let number = 1;

    if (
      (prevChangeLogs &&
        prevChangeLogs[item]?.toString() &&
        currentChangeLogs[item]?.toString() &&
        typeof currentChangeLogs[item] !== 'object' &&
        !isEqual(
          currentChangeLogs[item]?.toString(),
          prevChangeLogs[item]?.toString(),
        )) ||
      (prevChangeLogs &&
        prevChangeLogs[item]?.toString() &&
        currentChangeLogs[item]?.toString() &&
        typeof currentChangeLogs[item] === 'object' &&
        !isEqual(currentChangeLogs[item], prevChangeLogs[item])) ||
      (prevChangeLogs &&
        !prevChangeLogs[item]?.toString() &&
        currentChangeLogs[item]?.toString()) ||
      (!prevChangeLogs &&
        currentChangeLogs &&
        currentChangeLogs[item]?.toString())
    ) {
      updated = true;
      number = 0;
    }

    fields.push({
      name: item,
      updated,
      number,
    });
  });

  return fields;
};

const getChangedAndUnchangedFields = ({
  selectedChangeLogFields,
  selectedChangeLog,
  setSelectedField,
  setUnchangedSelectedField,
}: {
  setSelectedField: (
    data: {
      name: string;
      prev: string;
      current: string;
      prevType?: 'string' | 'object' | 'objectArray';
      currentType?: 'string' | 'object' | 'objectArray';
    }[],
  ) => void;
  setUnchangedSelectedField: (
    data: {
      name: string;
      value: string;
      type: 'string' | 'object' | 'objectArray';
    }[],
  ) => void;
  selectedChangeLogFields?: {
    name: string;
    updated: boolean;
    number: number;
    prevType?: 'string' | 'object' | 'objectArray';
    currentType?: 'string' | 'object' | 'objectArray';
  }[];
  selectedChangeLog?: ChangeLog;
}) => {
  const updatedFieldsValues: {
    name: string;
    prev: string;
    current: string;
    prevType?: 'string' | 'object' | 'objectArray';
    currentType?: 'string' | 'object' | 'objectArray';
  }[] = [];

  const unchangedFieldsValues: {
    name: string;
    value: string;
    type: 'string' | 'object' | 'objectArray';
  }[] = [];

  selectedChangeLogFields?.forEach((item) => {
    if (item.updated) {
      let prev: string | any = '-';
      let current: string | any = '-';
      let prevType: 'string' | 'object' | 'objectArray' = 'string';
      let currentType: 'string' | 'object' | 'objectArray' = 'string';

      if (
        selectedChangeLog?.prev &&
        selectedChangeLog?.prev[item.name]?.toString()
      ) {
        if (Array.isArray(selectedChangeLog?.prev[item.name])) {
          if (typeof selectedChangeLog?.prev[item.name][0] === 'object') {
            const objectsFieldsArray: any[] = [];

            selectedChangeLog?.prev[item.name]?.forEach((itemValue: any) => {
              objectsFieldsArray.push(itemValue);
            });

            prev = objectsFieldsArray;
            prevType = 'objectArray';
          } else {
            prev = selectedChangeLog?.prev[item.name]?.join(', ');
            prevType = 'string';
          }
        } else if (typeof selectedChangeLog?.prev[item.name] === 'object') {
          prevType = 'object';
          prev = selectedChangeLog?.prev[item.name];
        } else {
          prev = selectedChangeLog?.prev[item.name];
          prevType = 'string';
        }
      }

      if (selectedChangeLog?.current[item.name]?.toString()) {
        if (Array.isArray(selectedChangeLog?.current[item.name])) {
          if (typeof selectedChangeLog?.current[item.name][0] === 'object') {
            const objectsFieldsArray: any[] = [];

            selectedChangeLog?.current[item.name]?.forEach((itemValue: any) => {
              objectsFieldsArray.push(itemValue);
            });

            currentType = 'objectArray';
            current = objectsFieldsArray;
          } else {
            current = selectedChangeLog?.current[item.name]?.join(', ');
            currentType = 'string';
          }
        } else if (typeof selectedChangeLog?.current[item.name] === 'object') {
          currentType = 'object';
          current = selectedChangeLog?.current[item.name];
        } else {
          current = selectedChangeLog?.current[item.name];
          currentType = 'string';
        }
      }

      updatedFieldsValues.push({
        name: item.name,
        prev,
        current,
        prevType,
        currentType,
      });
    } else {
      let current: string | any = '-';
      let type: 'string' | 'object' | 'objectArray' = 'string';

      if (selectedChangeLog?.current[item.name]?.toString()) {
        if (Array.isArray(selectedChangeLog?.current[item.name])) {
          if (typeof selectedChangeLog?.current[item.name][0] === 'object') {
            const objectsFieldsArray: any[] = [];

            selectedChangeLog?.current[item.name]?.forEach((itemValue: any) => {
              objectsFieldsArray.push(itemValue);
            });

            current = objectsFieldsArray;
            type = 'objectArray';
          } else {
            current = selectedChangeLog?.current[item.name]?.join(', ');
            type = 'string';
          }
        } else if (typeof selectedChangeLog?.current[item.name] === 'object') {
          type = 'object';
          current = selectedChangeLog?.current[item.name];
        } else {
          current = selectedChangeLog?.current[item.name];
          type = 'string';
        }
      }

      unchangedFieldsValues.push({
        name: item.name,
        value: current,
        type,
      });
    }
  });

  setSelectedField(updatedFieldsValues);
  setUnchangedSelectedField(unchangedFieldsValues);

  return {
    updatedFieldsValues,
    unchangedFieldsValues,
  };
};

const ChangeLogs = () => {
  const [entityType, setEntityType] = useState<string[]>();
  const [entityId, setEntityId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [selectedChangeLog, setSelectedChangeLog] = useState<ChangeLog>();
  const [selectedChangeLogFields, setSelectedChangeLogFields] = useState<
    { name: string; updated: boolean; number: number }[]
  >([]);
  const [selectedField, setSelectedField] = useState<
    {
      name: string;
      prev: string | any;
      current: string | any;
      prevType?: 'string' | 'object' | 'objectArray';
      currentType?: 'string' | 'object' | 'objectArray';
    }[]
  >();
  const [unchangedSelectedField, setUnchangedSelectedField] = useState<
    {
      name: string;
      value: string | any;
      type: 'string' | 'object' | 'objectArray';
    }[]
  >();
  const [showAllFields, setShowAllFields] = useState(false);
  const [showSearchField, setShowSearchField] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [fieldSearchText, setFieldSearchText] = useState('');
  const [showFieldSearchText, setShowFieldSearchText] = useState(false);
  const [nextChangeLogsLoading, setNextChangeLogsLoading] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();

  const {
    changeLogs: { changeLogs, pagination },
    administrationDictionaries: { activePermissions, roles, users },
  } = useAppSelector((state) => state);

  const prevEntityTypes = usePrevious(entityType);

  const [getChangeLogs, { isLoading, isFetching }] =
    useLazyGetChangeLogsQuery();
  const [
    getChangeLogById,
    { isLoading: changeLogIsLoading, isFetching: changeLogIsFetching },
  ] = useLazyGetChangeLogByIdQuery();

  const [getEmployeesBaseDictionaries, { isLoading: dictionariesIsLoading }] =
    useLazyGetEmployeesBaseDictionariesQuery();
  const [getEmployeeDictionaries] = useLazyGetEmployeesDictionariesQuery();

  const { search } = useLocation();
  const navigate = useNavigate();

  const getLogsListAction = (query: string) => {
    getChangeLogs(`${query}&take=30`);
    navigate(query);
  };

  const onClearList = () => {
    dispatch(clearChangeLogs());

    setEntityType([]);
    setEntityId('');
    setEmployeeId('');
    setSelectedField(undefined);
    setOnlyChanged(false);
    setUnchangedSelectedField(undefined);
    setSelectedChangeLogFields([]);
    setShowAllFields(false);
    setShowSearchField(false);
    setSearchText('');
    setFieldSearchText('');
    setShowFieldSearchText(false);

    navigate('');
  };

  const onScroll = async (scroll: number) => {
    if (scrollRef && scrollRef.current) {
      if (
        scroll >
          scrollRef.current.scrollHeight -
            scrollRef.current.clientHeight -
            100 &&
        pagination?.hasNextPage
      ) {
        setNextChangeLogsLoading(true);

        let query = `?entityType=${entityType?.join('&entityType=')}&cursor=${
          pagination.endCursor
        }&take=30`;

        if (entityId) {
          query = `${query}&entityId=${entityId}`;
        }

        if (employeeId) {
          query = `${query}&employeeId=${query}`;
        }

        const response = await getChangeLogs(query);

        if (response?.data) {
          setNextChangeLogsLoading(false);
        }
      }
    }
  };

  const getChangeLogByIdAction = useCallback(async (id: string) => {
    const response = (await getChangeLogById(id)) as unknown as {
      data: { data: ChangeLog };
    };

    if (response.data.data) {
      setSelectedChangeLog(response.data.data);
    }
  }, []);

  const onSearchFeild = (text: string) => {
    setFieldSearchText(text);

    if (text) {
      const { updatedFieldsValues, unchangedFieldsValues } =
        getChangedAndUnchangedFields({
          selectedChangeLogFields,
          selectedChangeLog,
          setSelectedField,
          setUnchangedSelectedField,
        });
      const nextFields = updatedFieldsValues?.filter((item) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      );

      const nextUnchangedFields = unchangedFieldsValues?.filter((item) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      );

      setSelectedField(nextFields);
      setUnchangedSelectedField(nextUnchangedFields);
    } else {
      getChangedAndUnchangedFields({
        selectedChangeLogFields,
        selectedChangeLog,
        setSelectedField,
        setUnchangedSelectedField,
      });
    }
  };

  const onSearch = (text: string) => {
    setSearchText(text);

    if (text) {
      const fields = getSelectedChangeLogFields(selectedChangeLog as ChangeLog);

      const nextFields = fields?.filter((item) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      );

      setSelectedChangeLogFields(
        nextFields?.sort((a, b) => a.number - b.number),
      );
    } else {
      const fields = getSelectedChangeLogFields(selectedChangeLog as ChangeLog);

      setSelectedChangeLogFields(fields?.sort((a, b) => a.number - b.number));
    }
  };

  useEffect(() => {
    if (!roles) {
      getEmployeesBaseDictionaries();
    }
  }, [roles]);

  useEffect(() => {
    if (
      !users &&
      activePermissions &&
      activePermissions?.includes(PermissionEnum.USERS_LIST)
    ) {
      getEmployeeDictionaries();
    }
  }, [users, activePermissions]);

  useEffect(() => {
    if (
      search &&
      !entityType &&
      activePermissions &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      const parsed = queryString.parse(search);

      if (parsed) {
        let query: string;

        if (Array.isArray(parsed.entityType)) {
          query = `?take=30&entityType=${(parsed?.entityType as string[]).join(
            `&entityType=`,
          )}`;
          setEntityType(parsed.entityType as string[]);
        } else {
          query = `?take=30&entityType=${parsed.entityType}`;
          setEntityType([parsed.entityType as string]);
        }

        if (parsed.entityId) {
          setEntityId(parsed.entityId as string);

          query = `${query}&entityId=${parsed.entityId}`;
        }

        if (parsed.employeeId) {
          setEmployeeId(parsed.employeeId as string);

          query = `${query}&employeeId=${parsed.employeeId}`;
        }

        getChangeLogs(query);
      }
    }
  }, [search, entityType, activePermissions]);

  useEffect(() => {
    if (selectedChangeLog) {
      const fields = getSelectedChangeLogFields(selectedChangeLog);

      setSelectedChangeLogFields(fields?.sort((a, b) => a.number - b.number));

      if (search) {
        const parsed = queryString.parse(search);

        if (
          (parsed.field || parsed.unchangedField) &&
          !selectedField &&
          !unchangedSelectedField
        ) {
          if (parsed.field !== 'all') {
            const currentField = fields?.find(
              (item) => item.name === parsed.field || parsed.unchangedField,
            );

            if (currentField) {
              if (parsed.field && !parsed.unchangedField) {
                let prev: string | any = '-';
                let current: string | any = '-';
                let prevType: 'string' | 'object' | 'objectArray' = 'string';
                let currentType: 'string' | 'object' | 'objectArray' = 'string';

                if (
                  selectedChangeLog?.prev &&
                  selectedChangeLog?.prev[currentField.name]
                ) {
                  if (
                    Array.isArray(selectedChangeLog?.prev[currentField.name])
                  ) {
                    if (
                      typeof selectedChangeLog?.prev[currentField.name][0] ===
                      'object'
                    ) {
                      const objectsFieldsArray: any[] = [];

                      selectedChangeLog?.prev[currentField.name]?.forEach(
                        (itemValue: any) => {
                          objectsFieldsArray.push(itemValue);
                        },
                      );

                      prev = objectsFieldsArray;
                      prevType = 'objectArray';
                    } else {
                      prev =
                        selectedChangeLog?.prev[currentField.name]?.join(', ');
                      prevType = 'string';
                    }
                  } else if (
                    typeof selectedChangeLog?.prev[currentField.name] ===
                    'object'
                  ) {
                    prevType = 'object';
                    prev = selectedChangeLog?.prev[currentField.name];
                  } else {
                    prev = selectedChangeLog?.prev[currentField.name];
                    prevType = 'string';
                  }
                }

                if (selectedChangeLog?.current[currentField.name]) {
                  if (
                    Array.isArray(selectedChangeLog?.current[currentField.name])
                  ) {
                    if (
                      typeof selectedChangeLog?.current[
                        currentField.name
                      ][0] === 'object'
                    ) {
                      const objectsFieldsArray: any[] = [];

                      selectedChangeLog?.current[currentField.name]?.forEach(
                        (itemValue: any) => {
                          objectsFieldsArray.push(itemValue);
                        },
                      );

                      current = objectsFieldsArray;
                      currentType = 'objectArray';
                    } else {
                      current =
                        selectedChangeLog?.current[currentField.name]?.join(
                          ', ',
                        );
                      currentType = 'string';
                    }
                  } else if (
                    typeof selectedChangeLog?.current[currentField.name] ===
                    'object'
                  ) {
                    currentType = 'object';
                    current = selectedChangeLog?.current[currentField.name];
                  } else {
                    current = selectedChangeLog?.current[currentField.name];
                    currentType = 'string';
                  }
                }

                setSelectedField([
                  {
                    name: currentField.name,
                    prev,
                    current,
                    prevType,
                    currentType,
                  },
                ]);
              } else if (parsed.unchangedField) {
                let current: string | any = '-';
                let type: 'string' | 'object' | 'objectArray' = 'string';

                if (selectedChangeLog?.current[currentField.name]) {
                  if (
                    Array.isArray(selectedChangeLog?.current[currentField.name])
                  ) {
                    if (
                      typeof selectedChangeLog?.current[
                        currentField.name
                      ][0] === 'object'
                    ) {
                      const objectsFieldsArray: any[] = [];

                      selectedChangeLog?.current[currentField.name]?.forEach(
                        (itemValue: any) => {
                          objectsFieldsArray.push(itemValue);
                        },
                      );

                      current = objectsFieldsArray;
                      type = 'objectArray';
                    } else {
                      current =
                        selectedChangeLog?.current[currentField.name]?.join(
                          ', ',
                        );
                      type = 'string';
                    }
                  } else if (
                    typeof selectedChangeLog?.current[currentField.name] ===
                    'object'
                  ) {
                    type = 'object';
                    current = selectedChangeLog?.current[currentField.name];
                  } else {
                    current = selectedChangeLog?.current[currentField.name];
                    type = 'string';
                  }
                }

                setUnchangedSelectedField([
                  {
                    name: currentField.name,
                    value: current,
                    type,
                  },
                ]);
              }
            }
          } else if (parsed.field === 'all') {
            getChangedAndUnchangedFields({
              selectedChangeLogFields: fields,
              selectedChangeLog,
              setSelectedField,
              setUnchangedSelectedField,
            });
            setShowAllFields(true);
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (
            (parsed.field === 'all' && !parsed.unchangedField) ||
            (!parsed.field && !parsed.unchangedField)
          ) {
            getChangedAndUnchangedFields({
              selectedChangeLogFields: fields,
              selectedChangeLog,
              setSelectedField,
              setUnchangedSelectedField,
            });

            setShowAllFields(true);

            navigate(
              `${search
                ?.split('&')
                ?.filter((param) => !param.includes('field'))
                .join('&')}&field=all`,
            );
          }
        }
      }
    }
  }, [selectedChangeLog, search]);

  useEffect(() => {
    if (changeLogs && search) {
      const parsed = queryString.parse(search);

      if (parsed.changeLogId) {
        const currentChangeLog = changeLogs?.find(
          (item) => item.id === parsed.changeLogId,
        );

        if (currentChangeLog) {
          setSelectedChangeLog(currentChangeLog);
        } else {
          getChangeLogByIdAction(parsed.changeLogId as string);
        }
      }
    }
  }, [changeLogs]);

  useEffect(() => {
    if (prevEntityTypes?.length && !entityType?.length) {
      onClearList();
    }
  }, [prevEntityTypes, entityType]);

  let content;

  if (dictionariesIsLoading) {
    content = (
      <div className={cn.changeLogsLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !dictionariesIsLoading &&
    activePermissions &&
    activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
  ) {
    const groupedLogs = groupBy(changeLogs, (match) =>
      new Date(match.createdAt).toLocaleDateString(),
    );

    content = (
      <>
        <div className={cn.changeLogsTop}>
          <div className={cn.changeLogsTitle}>Change Logs</div>
          <div className={cn.changeLogsControls}>
            <EntitySelector
              selectedEntity={entityType}
              onChange={(data) => {
                setEntityType(data);

                if (data.length) {
                  getLogsListAction(`?entityType=${data.join('&entityType=')}`);

                  if (selectedChangeLog) {
                    setSelectedChangeLog(undefined);
                  }

                  if (selectedField) {
                    setSelectedField(undefined);
                  }

                  if (onlyChanged) {
                    setOnlyChanged(false);
                  }

                  if (unchangedSelectedField) {
                    setUnchangedSelectedField(undefined);
                  }

                  if (selectedChangeLogFields.length) {
                    setSelectedChangeLogFields([]);
                  }

                  if (showAllFields) {
                    setShowAllFields(false);
                  }

                  if (showSearchField) {
                    setShowSearchField(false);
                  }

                  if (searchText) {
                    setSearchText('');
                  }

                  if (fieldSearchText) {
                    setFieldSearchText('');
                  }

                  if (showFieldSearchText) {
                    setShowFieldSearchText(false);
                  }
                } else {
                  navigate('');
                  dispatch(clearChangeLogs());
                  setEntityId('');
                  setEmployeeId('');
                  setEntityType([]);
                }
              }}
            />
            <div className={cn.entityIdWrapper}>
              <InputText
                name="entityId"
                value={entityId}
                labelClassName={cn.entityIdInput}
                disabled={!entityType?.length}
                onChange={(e) => {
                  clearTimeout(entityIdTimeout);
                  setEntityId(e.target.value);

                  entityIdTimeout = setTimeout(() => {
                    let query = `?entityType=${entityType?.join(
                      '&entityType=',
                    )}`;

                    if (employeeId) {
                      query = `${query}&employeeId=${entityId}`;
                    }

                    if (e.target.value) {
                      query = `${query}&entityId=${e.target.value}`;
                    } else {
                      query = query
                        ?.split('&')
                        ?.filter((item) => !item.includes('entityId'))
                        ?.join('&');
                    }

                    getLogsListAction(query);
                  }, 700);
                }}
                placeholder="Entity ID"
              />
              {entityId && (
                <button
                  type="button"
                  className={cn.clearInputButton}
                  aria-label="clear input"
                  onClick={() => {
                    let query = `?entityType=${entityType?.join(
                      '&entityType=',
                    )}`;

                    if (employeeId) {
                      query = `${query}&employeeId=${entityId}`;
                    }

                    getLogsListAction(query);
                    setEntityId('');
                  }}
                >
                  <IconClose width={14} height={14} />
                </button>
              )}
            </div>

            <Autocomplete
              name="employeeId"
              value={employeeId}
              list={users || []}
              placeholder="Select user"
              className={cn.userSelect}
              disabled={!entityType?.length}
              onChange={(e) => {
                setEmployeeId(e.target.value as string);

                let query = `?entityType=${entityType?.join('&entityType=')}`;

                if (entityId) {
                  query = `${query}&entityId=${entityId}`;
                }

                if (e.target.value) {
                  query = `${query}&employeeId=${e.target.value}`;
                } else {
                  query = query
                    ?.split('&')
                    ?.filter((item) => !item.includes('employeeId'))
                    ?.join('&');
                }

                getLogsListAction(query);
              }}
              withClearButton
            />
          </div>
        </div>
        <div className={cn.entityTypesFilter}>
          {entityType?.map((item) => (
            <div className={cn.selectedEntity} key={item}>
              <span>Entity: </span>
              {item
                .split('_')
                .map(
                  (value) =>
                    `${value.charAt(0)}${value.slice(1).toLowerCase()}`,
                )
                .join(' ')
                .replace('Issue', 'Task')}
              <button
                type="button"
                className={cn.clearEntityButton}
                aria-label="Clear button"
                onClick={() => {
                  const filtered = entityType?.filter((type) => type !== item);
                  const query = `?entityType=${filtered?.join('&entityType=')}`;

                  if (filtered?.length) {
                    getLogsListAction(query);
                  }

                  setEntityType(filtered);
                }}
              >
                <IconClose width={14} height={14} />
              </button>
            </div>
          ))}
          {entityType && entityType?.length > 0 && (
            <button
              type="button"
              className={cn.clearAllButton}
              onClick={onClearList}
            >
              Clear all
            </button>
          )}
        </div>
        <div className={cn.changeLogsContent}>
          <div className={cn.changeLogsColumn}>
            <div className={cn.changeLogsColumnTop}>
              <div className={cn.changeLogsColumnTitle}>Date</div>
              <DateInputRange
                placeholder="All date"
                className={cn.logsDatepicker}
                onChangeRange={(dates) => setDateRange(dates)}
                values={dateRange?.map((item) => item.toISOString())}
                onReset={() => setDateRange([])}
              />
            </div>
            {!nextChangeLogsLoading && (isLoading || isFetching) && (
              <div className={cn.changeLogsLoading}>
                <IconLoader />
              </div>
            )}
            {((!isLoading && !isFetching) ||
              isLoading ||
              (isFetching && nextChangeLogsLoading)) && (
              <div
                className={cn.changeLogsBottom}
                ref={scrollRef}
                // @ts-ignore
                onScroll={(e) => onScroll(e.target.scrollTop)}
              >
                {Object.keys(groupedLogs)?.map((group) => {
                  const firstLogDate = new Date(
                    groupedLogs[group][0].createdAt,
                  );

                  return (
                    <div className={cn.changeLogsGroup} key={group}>
                      <div className={cn.changeLogsGroupDate}>
                        {`${
                          firstLogDate.getDate() < 0 ? '0' : ''
                        }${firstLogDate.getDate()} ${
                          months[firstLogDate.getMonth()]
                        }`}
                      </div>
                      {groupedLogs[group].map((item) => {
                        let employee = '-';

                        if (
                          item.employeeId &&
                          item.employeeId !== 'undefined' &&
                          item.employeeId !== 'null'
                        ) {
                          employee = users?.find(
                            (user) => user.value === item.employeeId,
                          )?.name as string;
                        }

                        let changeLogEntityType = item.entityType
                          .split('_')
                          ?.map(
                            (type) =>
                              `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                          )
                          ?.join(' ');

                        if (item?.prev?.type) {
                          const typeName = item?.prev?.type
                            ?.split('_')
                            ?.map(
                              (type: string) =>
                                `${type.charAt(0)}${type
                                  .slice(1)
                                  .toLowerCase()}`,
                            )
                            ?.join(' ');

                          changeLogEntityType = `${changeLogEntityType} ${typeName}`;
                        } else if (item?.current?.type) {
                          const typeName = item?.current?.type
                            ?.split('_')
                            ?.map(
                              (type: string) =>
                                `${type.charAt(0)}${type
                                  .slice(1)
                                  .toLowerCase()}`,
                            )
                            .join(' ');

                          changeLogEntityType = `${changeLogEntityType} ${typeName}`;
                        }

                        return (
                          <button
                            type="button"
                            className={`${cn.changeLogButton} ${
                              selectedChangeLog?.id === item.id ? cn.active : ''
                            }`}
                            key={item.id}
                            onClick={() => {
                              setSelectedChangeLog(item);
                              setSelectedField(undefined);
                              setUnchangedSelectedField(undefined);

                              if (showAllFields) {
                                setShowAllFields(false);
                              }

                              if (fieldSearchText) {
                                setFieldSearchText('');
                              }

                              if (showFieldSearchText) {
                                setShowFieldSearchText(false);
                              }

                              if (onlyChanged) {
                                setOnlyChanged(false);
                              }

                              navigate(
                                `${search
                                  ?.split('&')
                                  ?.filter(
                                    (param) =>
                                      !param.includes('changeLogId') &&
                                      !param.includes('field') &&
                                      !param.includes('unchangedField'),
                                  )
                                  ?.join('&')}&changeLogId=${item.id}`,
                              );
                            }}
                          >
                            <div className={cn.changeLogDate}>
                              {formatDate(item.createdAt, true)}
                            </div>
                            <div className={cn.changeLogInfo}>
                              <div className={cn.changeLogType}>
                                {changeLogEntityType?.replace('Issue', 'Task')}
                              </div>
                              <div className={cn.changeLogEmployee}>
                                {employee}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
                {nextChangeLogsLoading && (
                  <div className={cn.nextChangeLogsLoader}>
                    <IconLoader />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={cn.changeLogsColumn}>
            <div className={cn.changeLogsColumnTop}>
              {showSearchField && (
                <InputText
                  size={InputSize.SMALL}
                  name="search"
                  placeholder="Enter field name"
                  labelClassName={cn.changeLogSearchField}
                  value={searchText}
                  onChange={(e) => onSearch(e.target.value)}
                />
              )}
              {!showSearchField && (
                <div className={cn.changeLogsColumnTitle}>Fields</div>
              )}
              {selectedChangeLog && changeLogs && changeLogs?.length > 0 && (
                <button
                  type="button"
                  className={cn.searchButton}
                  onClick={() => {
                    setShowSearchField(!showSearchField);

                    if (showSearchField) {
                      onSearch('');
                    }
                  }}
                >
                  {showSearchField ? <IconClose /> : <IconSearch />}
                </button>
              )}
            </div>
            {selectedChangeLog && changeLogs && changeLogs?.length > 0 && (
              <button
                type="button"
                className={`${cn.selectAllFields} ${
                  showAllFields ? cn.active : ''
                }`}
                onClick={() => {
                  getChangedAndUnchangedFields({
                    selectedChangeLogFields,
                    selectedChangeLog,
                    setSelectedField,
                    setUnchangedSelectedField,
                  });

                  setShowAllFields(true);
                  setOnlyChanged(false);

                  navigate(
                    `${search
                      ?.split('&')
                      ?.filter((param) => !param.includes('field'))
                      .join('&')}&field=all`,
                  );
                }}
              >{`All changed in ${selectedChangeLog?.entityType
                .split('_')
                ?.map(
                  (type) => `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                )
                ?.join(' ')
                .replace('Issue', 'Task')} ${
                selectedChangeLog?.prev?.type
                  ? `${selectedChangeLog?.prev?.type
                      ?.split('_')
                      ?.map(
                        (type: string) =>
                          `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                      )
                      ?.join(' ')}`
                  : ''
              } ${
                !selectedChangeLog?.prev?.type &&
                selectedChangeLog?.current?.type
                  ? `${selectedChangeLog?.current?.type
                      ?.split('_')
                      ?.map(
                        (type: string) =>
                          `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                      )
                      ?.join(' ')}`
                  : ''
              }`}</button>
            )}
            {(changeLogIsFetching || changeLogIsLoading) && (
              <div className={cn.changeLogsLoading}>
                <IconLoader />
              </div>
            )}
            {!changeLogIsLoading &&
              !changeLogIsFetching &&
              selectedChangeLog &&
              changeLogs &&
              changeLogs?.length > 0 && (
                <div className={`${cn.changeLogsBottom} ${cn.smallHeight}`}>
                  {selectedChangeLogFields?.map((item) => (
                    <button
                      className={`${cn.changeLogField} ${
                        !showAllFields &&
                        selectedField?.length === 1 &&
                        selectedField[0]?.name === item.name
                          ? cn.active
                          : ''
                      }`}
                      key={item.name}
                      type="button"
                      onClick={() => {
                        if (item.updated) {
                          let prev: string | any = '-';
                          let current: string | any = '-';
                          let prevType: 'string' | 'object' | 'objectArray' =
                            'string';
                          let currentType: 'string' | 'object' | 'objectArray' =
                            'string';

                          if (
                            selectedChangeLog?.prev &&
                            selectedChangeLog?.prev[item.name]?.toString()
                          ) {
                            if (
                              Array.isArray(selectedChangeLog?.prev[item.name])
                            ) {
                              if (
                                typeof selectedChangeLog?.prev[item.name][0] ===
                                'object'
                              ) {
                                const objectsFieldsArray: any[] = [];

                                selectedChangeLog?.prev[item.name]?.forEach(
                                  (itemValue: any) => {
                                    objectsFieldsArray.push(itemValue);
                                  },
                                );

                                prevType = 'objectArray';
                                prev = objectsFieldsArray;
                              } else {
                                prev =
                                  selectedChangeLog?.prev[item.name]?.join(
                                    ', ',
                                  );
                                prevType = 'string';
                              }
                            } else if (
                              typeof selectedChangeLog?.prev[item.name] ===
                              'object'
                            ) {
                              prevType = 'object';
                              prev = selectedChangeLog?.prev[item.name];
                            } else {
                              prev = selectedChangeLog?.prev[item.name];
                              prevType = 'string';
                            }
                          }

                          if (
                            selectedChangeLog?.current[item.name]?.toString()
                          ) {
                            if (
                              Array.isArray(
                                selectedChangeLog?.current[item.name],
                              )
                            ) {
                              if (
                                typeof selectedChangeLog?.current[
                                  item.name
                                ][0] === 'object'
                              ) {
                                const objectsFieldsArray: any[] = [];

                                selectedChangeLog?.current[item.name]?.forEach(
                                  (itemValue: any) => {
                                    objectsFieldsArray.push(itemValue);
                                  },
                                );

                                currentType = 'objectArray';
                                current = objectsFieldsArray;
                              } else {
                                current =
                                  selectedChangeLog?.current[item.name]?.join(
                                    ', ',
                                  );
                                currentType = 'string';
                              }
                            } else if (
                              typeof selectedChangeLog?.current[item.name] ===
                              'object'
                            ) {
                              currentType = 'object';
                              current = selectedChangeLog?.current[item.name];
                            } else {
                              current = selectedChangeLog?.current[item.name];
                              currentType = 'string';
                            }
                          }

                          setSelectedField([
                            {
                              name: item.name,
                              prev,
                              current,
                              prevType,
                              currentType,
                            },
                          ]);

                          setUnchangedSelectedField(undefined);

                          setShowAllFields(false);

                          navigate(
                            `${search
                              ?.split('&')
                              ?.filter(
                                (param) =>
                                  !param.includes('field') &&
                                  !param.includes('unchangedField'),
                              )
                              .join('&')}&field=${item.name}`,
                          );
                        } else {
                          let current: string | any = '-';
                          let type: 'string' | 'object' | 'objectArray' =
                            'string';

                          if (
                            selectedChangeLog?.current[item.name]?.toString()
                          ) {
                            if (
                              Array.isArray(
                                selectedChangeLog?.current[item.name],
                              )
                            ) {
                              if (
                                typeof selectedChangeLog?.current[
                                  item.name
                                ][0] === 'object'
                              ) {
                                const objectsFieldsArray: any[] = [];

                                selectedChangeLog?.current[item.name]?.forEach(
                                  (itemValue: any) => {
                                    objectsFieldsArray.push(itemValue);
                                  },
                                );
                                type = 'objectArray';
                                current = objectsFieldsArray;
                              } else {
                                current =
                                  selectedChangeLog?.current[item.name]?.join(
                                    ', ',
                                  );
                                type = 'string';
                              }
                            } else if (
                              typeof selectedChangeLog?.current[item.name] ===
                              'object'
                            ) {
                              type = 'object';
                              current = selectedChangeLog?.current[item.name];
                            } else {
                              current =
                                selectedChangeLog?.current[
                                  item.name
                                ]?.toString();

                              type = 'string';
                            }
                          }

                          setSelectedField(undefined);
                          setShowAllFields(false);

                          setUnchangedSelectedField([
                            {
                              name: item.name,
                              value: current,
                              type,
                            },
                          ]);

                          navigate(
                            `${search
                              ?.split('&')
                              ?.filter(
                                (param) =>
                                  !param.includes('field') &&
                                  !param.includes('unchangedField'),
                              )
                              .join('&')}&unchangedField=${item.name}`,
                          );
                        }

                        setShowFieldSearchText(false);
                      }}
                    >
                      {item.updated && (
                        <div className={cn.circleWrapper}>
                          <div className={cn.circle} />
                        </div>
                      )}
                      <div className={cn.changeLogFieldName}>
                        <Highlight
                          searchQuery={searchText}
                          text={item.name}
                          className={cn.changeLogHightlight}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              )}
          </div>
          <div className={`${cn.changeLogsColumn} ${cn.changeLogsValueColumn}`}>
            <div className={cn.changeLogsColumnTop}>
              {showFieldSearchText && (
                <InputText
                  size={InputSize.SMALL}
                  name="fieldSearch"
                  placeholder="Enter field name"
                  labelClassName={cn.changeLogSearchField}
                  value={fieldSearchText}
                  onChange={(e) => onSearchFeild(e.target.value)}
                />
              )}
              {!showFieldSearchText && (
                <div className={cn.changeLogsColumnTitle}>Value</div>
              )}
              <div className={cn.changeColumnsRight}>
                {showAllFields && !showFieldSearchText && (
                  <div className={cn.onlyChangedWrapper}>
                    <ToggleInput
                      name="onlyChanged"
                      checked={onlyChanged}
                      onChange={(e) => setOnlyChanged(e.target.checked)}
                      title="Only Changed"
                    />
                  </div>
                )}

                {showAllFields && (
                  <button
                    type="button"
                    className={cn.searchButton}
                    onClick={() => {
                      setShowFieldSearchText(!showFieldSearchText);

                      if (showFieldSearchText) {
                        onSearchFeild('');
                      }
                    }}
                  >
                    {showFieldSearchText ? <IconClose /> : <IconSearch />}
                  </button>
                )}
              </div>
            </div>
            <div className={cn.changeLogsBottom}>
              {selectedField?.map((item) => (
                <div key={item.name}>
                  {item.prevType === 'string' &&
                    item.currentType === 'string' && (
                      <div className={cn.selectedField} key={item.name}>
                        <div className={cn.selectedFieldName}>
                          <Highlight
                            searchQuery={fieldSearchText}
                            text={item.name}
                            className={cn.changeLogHightlight}
                          />
                        </div>

                        <div className={cn.selectedFieldChangesWrapper}>
                          <div className={cn.selectedFieldChanges}>
                            <div className={cn.selectedFieldChangeValue}>
                              {item.prev.toString()}
                            </div>
                            <div
                              className={
                                cn.selectedFieldChangesSeparatorWrapper
                              }
                            >
                              <div className={cn.selectedFieldArrowWrapper}>
                                {item.prev &&
                                  item.prev !== '-' &&
                                  item.prev.toString() &&
                                  item.current &&
                                  item.current.toString() && (
                                    <IconChangeLogUpdated />
                                  )}
                                {item.prev === '-' &&
                                  item.current.toString() && (
                                    <IconChangeLogAdded />
                                  )}
                                {item.prev &&
                                  item.prev !== '-' &&
                                  !item.current.toString() && (
                                    <IconChangeLogDeleted />
                                  )}
                              </div>
                              <div
                                className={cn.selectedFieldChangesSeparator}
                              />
                            </div>
                            <div className={cn.selectedFieldChangeValue}>
                              {item?.current.toString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  {item.currentType === 'objectArray' &&
                    (item.current as any[])?.map((value) => {
                      const currentPrev = Array.isArray(item?.prev)
                        ? item?.prev?.find((prev: any) => value.id === prev.id)
                        : '';

                      let objectArrayContent = <div key={value.id} />;

                      if (item.name === 'periods') {
                        const updatedFields: any = {};

                        if (currentPrev) {
                          Object.keys(currentPrev).forEach((prev: any) => {
                            if (
                              prev !== 'startTime' &&
                              prev !== 'endTime' &&
                              currentPrev[prev] !== value[prev]
                            ) {
                              updatedFields[prev] = {
                                prev: currentPrev[prev],
                                current: value[prev],
                              };
                            }
                          });
                        }

                        objectArrayContent = (
                          <div className={cn.selectedField} key={value.id}>
                            <div className={cn.selectedFieldChangesWrapper}>
                              {((currentPrev &&
                                !isEqual(
                                  {
                                    startTime: currentPrev.startTime,
                                    endTime: currentPrev.endTime,
                                  },
                                  {
                                    startTime: value.startTime,
                                    endTime: value.endTime,
                                  },
                                )) ||
                                !currentPrev) && (
                                <div className={cn.selectedFieldChanges}>
                                  <div className={cn.selectedFieldChangeValue}>
                                    <div
                                      className={cn.selectedFieldPeriodValue}
                                    >
                                      <div className={cn.weekDay}>
                                        {currentPrev
                                          ? `${currentPrev.weekDay.charAt(
                                              0,
                                            )}${currentPrev.weekDay
                                              .slice(1)
                                              .toLowerCase()}`
                                          : ''}
                                      </div>
                                      {currentPrev && (
                                        <div className={cn.time}>
                                          {`${currentPrev.startTime} - ${currentPrev.endTime}`}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    className={
                                      cn.selectedFieldChangesSeparatorWrapper
                                    }
                                  >
                                    <div
                                      className={cn.selectedFieldArrowWrapper}
                                    >
                                      {currentPrev && <IconChangeLogUpdated />}
                                      {!currentPrev && <IconChangeLogAdded />}
                                    </div>
                                    <div
                                      className={
                                        cn.selectedFieldChangesSeparator
                                      }
                                    />
                                  </div>
                                  <div className={cn.selectedFieldChangeValue}>
                                    <div
                                      className={cn.selectedFieldPeriodValue}
                                    >
                                      <div className={cn.weekDay}>
                                        {`${value.weekDay.charAt(
                                          0,
                                        )}${value.weekDay
                                          .slice(1)
                                          .toLowerCase()}`}
                                      </div>
                                      <div className={cn.time}>
                                        {`${value.startTime} - ${value.endTime}`}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            {Object.keys(updatedFields)?.length > 0 &&
                              Object.keys(updatedFields)?.map((updated) => (
                                <div
                                  className={cn.updatedFieldRow}
                                  key={updated}
                                >
                                  <div className={cn.updatedFieldName}>
                                    {`${updated}:`}
                                  </div>
                                  <div className={cn.updatedFieldValues}>
                                    <div className={cn.updatedFieldValue}>
                                      {updatedFields[updated].prev}
                                    </div>
                                    <div className={cn.iconArrowWrapper}>
                                      <IconArrowRight width={12} height={12} />
                                    </div>
                                    <div className={cn.updatedFieldValue}>
                                      {updatedFields[updated].current}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        );
                      } else {
                        const updatedFields: any = {};

                        Object.keys(value).forEach((current: any) => {
                          if (
                            (currentPrev &&
                              currentPrev[current] !== value[current]) ||
                            !currentPrev
                          ) {
                            updatedFields[current] = {
                              prev: currentPrev[current] || '-',
                              current: value[current] || '-',
                            };
                          }
                        });

                        if (Object.keys(updatedFields).length) {
                          objectArrayContent = (
                            <div key={value.id}>
                              {Object.keys(updatedFields).map(
                                (updatedKey: any, index) => (
                                  <div
                                    className={cn.selectedField}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${updatedKey}-${index}`}
                                  >
                                    <div className={cn.selectedFieldName}>
                                      <Highlight
                                        searchQuery={fieldSearchText}
                                        text={`${item.name}: ${updatedKey}`}
                                        className={cn.changeLogHightlight}
                                      />
                                    </div>

                                    <div
                                      className={cn.selectedFieldChangesWrapper}
                                    >
                                      <div className={cn.selectedFieldChanges}>
                                        <div
                                          className={
                                            cn.selectedFieldChangeValue
                                          }
                                        >
                                          {updatedFields[
                                            updatedKey
                                          ]?.prev?.toString()}
                                        </div>
                                        <div
                                          className={
                                            cn.selectedFieldChangesSeparatorWrapper
                                          }
                                        >
                                          <div
                                            className={
                                              cn.selectedFieldArrowWrapper
                                            }
                                          >
                                            {updatedFields[updatedKey].prev !==
                                              '-' &&
                                              updatedFields[updatedKey]
                                                ?.current !== '-' && (
                                                <IconChangeLogUpdated />
                                              )}
                                            {updatedFields[updatedKey].prev ===
                                              '-' &&
                                              updatedFields[updatedKey]
                                                ?.current !== '-' && (
                                                <IconChangeLogAdded />
                                              )}
                                            {updatedFields[updatedKey].prev !==
                                              '-' &&
                                              updatedFields[updatedKey]
                                                ?.current === '-' && (
                                                <IconChangeLogDeleted />
                                              )}
                                          </div>
                                          <div
                                            className={
                                              cn.selectedFieldChangesSeparator
                                            }
                                          />
                                        </div>
                                        <div
                                          className={
                                            cn.selectedFieldChangeValue
                                          }
                                        >
                                          {updatedFields[
                                            updatedKey
                                          ]?.current?.toString()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          );
                        }
                      }

                      return objectArrayContent;
                    })}
                  {item.currentType === 'object' && (
                    <div>
                      {Object.keys(item?.current)?.map((key) => (
                        <div
                          className={cn.selectedField}
                          key={`${item.name}-${key}`}
                        >
                          <div className={cn.selectedFieldName}>
                            <Highlight
                              searchQuery={fieldSearchText}
                              text={`${
                                showAllFields ? `${item.name}: ` : ''
                              }${key}`}
                              className={cn.changeLogHightlight}
                            />
                          </div>
                          <div className={cn.selectedFieldChangesWrapper}>
                            <div className={cn.selectedFieldChanges}>
                              <div className={cn.selectedFieldChangeValue}>
                                {item.prev &&
                                item.prev[key] &&
                                typeof item.prev[key] === 'object' ? (
                                  <pre>
                                    {JSON.stringify(item.prev[key], null, 2)}
                                  </pre>
                                ) : (
                                  <div>{item.prev[key]?.toString()}</div>
                                )}
                                {!item.prev && '-'}
                              </div>
                              <div
                                className={
                                  cn.selectedFieldChangesSeparatorWrapper
                                }
                              >
                                <div className={cn.selectedFieldArrowWrapper}>
                                  {item.prev &&
                                    item.prev[key] &&
                                    item.prev[key] !== '-' &&
                                    item.prev[key].toString() &&
                                    item.current &&
                                    item.current[key].toString() && (
                                      <IconChangeLogUpdated />
                                    )}
                                  {(!item.prev ||
                                    (item.prev && !item.prev[key]) ||
                                    (item.prev && item.prev[key] === '-')) &&
                                    item.current.toString() && (
                                      <IconChangeLogAdded />
                                    )}
                                  {item.prev &&
                                    item.prev[key] &&
                                    item.prev[key] !== '-' &&
                                    !item.current[key].toString() && (
                                      <IconChangeLogDeleted />
                                    )}
                                </div>
                                <div
                                  className={cn.selectedFieldChangesSeparator}
                                />
                              </div>
                              <div className={cn.selectedFieldChangeValue}>
                                {item.current &&
                                item.current[key] &&
                                typeof item.current[key] === 'object' ? (
                                  <pre>
                                    {JSON.stringify(item.current[key], null, 2)}
                                  </pre>
                                ) : (
                                  <div>{item.current[key]?.toString()}</div>
                                )}
                                {!item.current && '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {!onlyChanged &&
                unchangedSelectedField?.map((item) => (
                  <div key={item.name}>
                    {item.type === 'string' && (
                      <div className={cn.unchangedField}>
                        <div className={cn.uncahgedSelectedFieldName}>
                          <Highlight
                            searchQuery={fieldSearchText}
                            text={item.name}
                            className={cn.changeLogHightlight}
                          />
                        </div>
                        <div className={cn.unchagedSelectedFieldValue}>
                          {item?.value.toString()}
                        </div>
                      </div>
                    )}
                    {item.type === 'objectArray' &&
                      (item.value as any[])?.map((value) => {
                        let contentUnchanged;

                        if (item.name === 'periods') {
                          contentUnchanged = (
                            <div className={cn.unchangedField} key={value.id}>
                              <div className={cn.unchangedFieldRow}>
                                <div className={cn.uncahngedFieldRowName}>
                                  {`${value.weekDay.charAt(0)}${value.weekDay
                                    .slice(1)
                                    .toLowerCase()}`}
                                </div>
                                <div
                                  className={cn.unchangedFieldRowTime}
                                >{`${value.startTime} - ${value.endTime}`}</div>
                              </div>
                            </div>
                          );
                        } else {
                          contentUnchanged = (
                            <div key={value.id}>
                              {Object.keys(value).map(
                                (keyValue: any, index) => (
                                  <div
                                    className={cn.unchangedField}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${keyValue}-${index}`}
                                  >
                                    <div
                                      className={cn.uncahgedSelectedFieldName}
                                    >
                                      <Highlight
                                        searchQuery={fieldSearchText}
                                        text={`${
                                          showAllFields ? `${item.name} :` : ''
                                        }${keyValue}`}
                                        className={cn.changeLogHightlight}
                                      />
                                    </div>
                                    <div
                                      className={cn.unchagedSelectedFieldValue}
                                    >
                                      {value[keyValue]}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          );
                        }

                        return contentUnchanged;
                      })}
                    {item.type === 'object' &&
                      Object.keys(item.value)?.map((keyValue) => (
                        <div className={cn.unchangedField} key={keyValue}>
                          <div className={cn.uncahgedSelectedFieldName}>
                            <Highlight
                              searchQuery={fieldSearchText}
                              text={`${
                                showAllFields ? `${item.name} :` : ''
                              }${keyValue}`}
                              className={cn.changeLogHightlight}
                            />
                          </div>

                          <div className={cn.unchagedSelectedFieldValue}>
                            {item.value &&
                              item.value[keyValue] &&
                              typeof item.value[keyValue] === 'object' && (
                                <pre>
                                  {JSON.stringify(
                                    item.value[keyValue],
                                    null,
                                    2,
                                  )}
                                </pre>
                              )}
                            {item.value &&
                              item.value[keyValue] &&
                              typeof item.value[keyValue] === 'string' &&
                              item.value[keyValue]}
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </>
    );
  } else if (
    !dictionariesIsLoading &&
    activePermissions &&
    !activePermissions.includes(PermissionEnum.AUDIT_LOG_LIST)
  ) {
    content = <PermissionDenied />;
  }

  return <div className={cn.changeLogs}>{content}</div>;
};

export default ChangeLogs;
